import React from 'react';
import { useAppContext } from '../../context/AppContext';
import { Users, Target, Zap, Globe, Briefcase, TrendingUp, Code, Shield, Cloud, Cog, ArrowRight } from 'lucide-react';

const WhatWeOfferPage = () => {
  const { setContactFormOpen } = useAppContext();
  
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 via-blue-800 to-purple-700 text-white py-20 md:py-32">
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl">
            <div className="mb-6">
              <span className="inline-block bg-yellow-400 text-black px-4 py-2 rounded-md text-sm font-bold uppercase tracking-wide">
                What We Offer
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              What We Offer
            </h1>
            <p className="text-xl md:text-2xl mb-8 leading-relaxed opacity-90">
              Comprehensive engineering solutions designed to accelerate your business growth and technical capabilities.
            </p>
            <button 
              onClick={() => setContactFormOpen(true)}
              className="bg-yellow-400 text-black font-bold py-4 px-8 rounded-md hover:bg-yellow-300 transition-colors inline-flex items-center group"
            >
              GET STARTED
              <ArrowRight size={20} className="ml-2 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Core Service Offerings</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We provide end-to-end engineering solutions tailored to your specific business needs and technical requirements.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {/* Dedicated Teams */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-blue-600">
              <div className="flex items-start mb-6">
                <div className="bg-blue-100 p-4 rounded-lg mr-6">
                  <Users className="text-blue-600" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">Dedicated Teams</h3>
                  <p className="text-gray-700 mb-6">
                    Build adaptable teams that align seamlessly with your workflows, culture, and delivery expectations.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">Tailored to your business needs</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">Seamless integration with your processes</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">Direct communication channels</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Build Operate Manage */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-green-500">
              <div className="flex items-start mb-6">
                <div className="bg-green-100 p-4 rounded-lg mr-6">
                  <Target className="text-green-600" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">Build-Operate-Manage (BOM)</h3>
                  <p className="text-gray-700 mb-6">
                    We set up, operate, and manage your India-based teams and operations — with flexibility to scale as your business grows.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">Complete team setup and management</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">Scalable and flexible operations</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-green-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">End-to-end operational oversight</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Engineering Services */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-purple-500">
              <div className="flex items-start mb-6">
                <div className="bg-purple-100 p-4 rounded-lg mr-6">
                  <Zap className="text-purple-600" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">Engineering Services</h3>
                  <p className="text-gray-700 mb-6">
                    Architecture, development, DevOps, QA automation, CI/CD, and full lifecycle ownership — tailored to your needs.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-purple-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">End-to-end development lifecycle</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-purple-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">DevOps and automation</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-purple-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">Quality assurance and testing</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Domain-Agnostic Expertise */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-orange-500">
              <div className="flex items-start mb-6">
                <div className="bg-orange-100 p-4 rounded-lg mr-6">
                  <Globe className="text-orange-600" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">Domain-Agnostic Expertise</h3>
                  <p className="text-gray-700 mb-6">
                    From SaaS to embedded systems, fintech to healthcare — our adaptable expertise empowers businesses across industries with robust, scalable solutions.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-orange-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">Cross-industry problem solving</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-orange-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">Flexible and technology-agnostic</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 bg-orange-600 rounded-full mr-3"></div>
                      <span className="text-sm text-gray-600">Future-ready and scalable</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tailored Hiring Strategy */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-red-500">
              <div className="flex items-start mb-6">
                <div className="bg-red-100 p-4 rounded-lg mr-6">
                  <Briefcase className="text-red-600" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">Tailored Hiring Strategy</h3>
                  <p className="text-gray-700 mb-6">
                    Collaborate with us to craft hiring strategies that align with your business goals, culture, and employee value proposition — ensuring you attract and retain the right talent.
                  </p>
                </div>
              </div>
            </div>

            {/* Performance Management Alignment */}
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow border-l-4 border-teal-500">
              <div className="flex items-start mb-6">
                <div className="bg-teal-100 p-4 rounded-lg mr-6">
                  <TrendingUp className="text-teal-600" size={32} />
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-4">Performance Management Alignment</h3>
                  <p className="text-gray-700 mb-6">
                    Streamline processes, and achieve operational excellence.
                  </p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Technical Expertise */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Technical Expertise</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our technical capabilities span across modern technologies and development practices.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Code className="text-blue-600" size={24} />
              </div>
              <h3 className="text-lg font-bold mb-3 text-red-600">Software Development</h3>
              <p className="text-gray-600 text-sm">Modern frameworks, best practices, and scalable applications</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="bg-green-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Cloud className="text-green-600" size={24} />
              </div>
              <h3 className="text-lg font-bold mb-3 text-red-600">Cloud & DevOps</h3>
              <p className="text-gray-600 text-sm">CI/CD pipelines, infrastructure as code, and cloud-native solutions</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="bg-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Shield className="text-purple-600" size={24} />
              </div>
              <h3 className="text-lg font-bold mb-3 text-red-600">Quality Assurance</h3>
              <p className="text-gray-600 text-sm">Automated testing, performance testing, and quality frameworks</p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm text-center">
              <div className="bg-orange-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Cog className="text-orange-600" size={24} />
              </div>
              <h3 className="text-lg font-bold mb-3 text-red-600">Embedded Systems</h3>
              <p className="text-gray-600 text-sm">Firmware development, IoT solutions, and embedded applications</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative bg-gradient-to-r from-green-900 via-blue-800 to-purple-700 text-white py-20">
        <div className="container mx-auto px-4 text-center relative">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Partner with Us?</h2>
          <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
            Let's discuss how our services can help accelerate your technology initiatives and business growth.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => setContactFormOpen(true)}
              className="bg-yellow-400 text-black font-bold py-4 px-8 rounded-md hover:bg-yellow-300 transition-colors"
            >
              Schedule a Consultation
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhatWeOfferPage;
